import axios, { AxiosInstance } from "axios";
import map from "lodash/map";
import { Unit } from "../../types/customization/box";
import { SelectionAlbum, SelectionPhoto } from "../../types/selection/selection";
import { FULL_MIN_SIZE } from "../../utils/selection/photo";
import { getVariableByEnv } from "../../utils/variableByEnv";
import BaseSelectionProvider from "./BaseSelectionProvider";

const FOTOM_URL = getVariableByEnv("REACT_APP_FOTOM_URL");

export default class UploadSelectionProvider extends BaseSelectionProvider {
  api: AxiosInstance;
  providerTag: "mobile_upload" | "web_upload";
  albums: {
    data: SelectionAlbum[];
  };
  albumPhotos: {
    [albumRef: string]: {
      data: SelectionPhoto[];
      paging?: number;
    };
  };

  constructor() {
    super();

    this.providerTag = "web_upload";

    const headers = {
      Accept: "application/json",
    };

    const baseURL = `${window.location.origin}/`;

    this.api = axios.create({
      baseURL,
      headers,
      withCredentials: true,
    });

    this.albums = { data: [] };
    this.albumPhotos = {};
  }

  hasAlbums(): boolean {
    return true;
  }
  hasMoreAlbums(): boolean {
    return false;
  }
  hasMoreAlbumPhotos(albumRef: string): boolean {
    return !!this.albumPhotos[albumRef].paging;
  }

  refreshAlbums(): void {
    this.albums = { data: [] };
    return;
  }
  refreshAlbumPhotos(): void {
    this.albumPhotos = {};
    return;
  }

  async getAlbums(): Promise<SelectionAlbum[]> {
    if (this.albums.data.length > 0) {
      return this.albums.data;
    }
    this.albums.data = await this.getAlbumsDistant();
    return this.albums.data;
  }

  async getAlbumsDistant(): Promise<SelectionAlbum[]> {
    const { data } = await this.api.get<AlbumResponse>("/upload_albums", {
      params: {
        provider_tag: this.providerTag,
      },
    });

    return map(data.upload_albums, (datum) => {
      return {
        coverPictureUrl: `${FOTOM_URL}/photos/min/${FULL_MIN_SIZE}/${datum.cover}.jpg`,
        name: datum.date,
        ref: datum.date,
      };
    });
  }

  async getAlbumPhotos(albumRef: string): Promise<SelectionPhoto[]> {
    if (this.albumPhotos[albumRef] && this.albumPhotos[albumRef].data.length > 0) {
      return this.albumPhotos[albumRef].data;
    }
    this.albumPhotos[albumRef] = { data: [] };
    this.albumPhotos[albumRef].data = await this.getAlbumPhotosDistant(albumRef);
    return this.albumPhotos[albumRef].data;
  }

  async getMoreAlbumPhotos(albumRef: string): Promise<SelectionPhoto[]> {
    if (!this.hasMoreAlbumPhotos(albumRef)) {
      return this.albumPhotos[albumRef].data;
    }
    const nextAlbumPhotos = await this.getAlbumPhotosDistant(albumRef, true);
    this.albumPhotos[albumRef].data = this.albumPhotos[albumRef].data.concat(nextAlbumPhotos);
    return this.albumPhotos[albumRef].data;
  }

  async getAlbumPhotosDistant(albumRef: string, next = false): Promise<SelectionPhoto[]> {
    const { data } = await this.api.get<AlbumPhotoResponse>(`/upload_albums/${albumRef}`, {
      params: {
        limit: 50,
        page: next && this.albumPhotos[albumRef].paging ? this.albumPhotos[albumRef].paging : 1,
        provider_tag: this.providerTag,
      },
    });

    this.albumPhotos[albumRef].paging = data.next_page ? data.next_page : undefined;

    return map(data.photos, (datum) => {
      return {
        key: `upload-${datum.sha1}`,

        height: { unit: Unit.PX, value: datum.height_px } as const,
        width: { unit: Unit.PX, value: datum.width_px } as const,

        provider: datum.provider_tag,
        providerRef: datum.sha1,

        fotomKey: datum.sha1,
      };
    });
  }
}

interface AlbumResponse {
  upload_albums: Array<{
    count: number;
    cover: string;
    date: string;
  }>;
}

interface AlbumPhotoResponse {
  date: string;
  photos: Array<{
    created_at: string | null;
    height_px: number;
    provider_fk: "string" | null;
    provider_tag: "upload" | "mobile_upload";
    sha1: string;
    taken_at: string | null;
    width_px: number;
  }>;
  next_page: number | null;
}
